import { useCallback, useEffect, useMemo } from 'react';
import useAiExecutionStatus from 'personalization-lib/hooks/variants/useAiExecutionStatus';
import { getAssetEditorUrl } from 'personalization-lib/utils/urls';
import { getUserId } from 'personalization-lib/utils/auth/userPermissions';
import { redirectToPage } from 'personalization-lib/utils/location';
import { showContentEditorCreateVariantSuccessAlert, showVariantGenerationSuccessAlert } from 'personalization-lib/utils/alerts';
export const MANUAL_SUCCESS_SESSION_KEY = 'personalization::manualSuccessId';
export const AI_SUCCESS_SESSION_KEY = 'personalization::aiSuccessId';
/**
 * Handles post-creation logic for variants created with content editor personalization provider, such as redirects, alerts, and session storage for alerts after redirects.
 * @param initialAssetData The initial asset data passed to the content editor provider (i.e. if we are on a variant page, pass variant data, not default's)
 */
const useContentEditorPostCreation = ({
  assetId,
  strategyId,
  assetName
}) => {
  const userId = getUserId();
  const {
    requestsInProgress,
    loading
  } = useAiExecutionStatus(strategyId, userId);
  useEffect(() => {
    const manualSuccessId = sessionStorage.getItem(MANUAL_SUCCESS_SESSION_KEY);
    const aiSuccessId = sessionStorage.getItem(AI_SUCCESS_SESSION_KEY);
    const currentAssetId = assetId.toString();
    if (manualSuccessId) {
      if (currentAssetId === manualSuccessId) {
        showContentEditorCreateVariantSuccessAlert();
        sessionStorage.removeItem(MANUAL_SUCCESS_SESSION_KEY);
      }
    } else if (aiSuccessId) {
      if (currentAssetId === aiSuccessId && assetName) {
        showVariantGenerationSuccessAlert({
          assetName
        });
        sessionStorage.removeItem(AI_SUCCESS_SESSION_KEY);
      }
    }
  }, [assetId, assetName]);
  const onManualCreateSuccess = useCallback(info => {
    const id = info.assetCoordinates.id.toString();
    sessionStorage.setItem(MANUAL_SUCCESS_SESSION_KEY, id);
    redirectToPage(getAssetEditorUrl(info.assetCoordinates));
  }, []);
  const onAiCreateSuccess = useCallback((info, isModalOpen) => {
    if (requestsInProgress > 1 || !isModalOpen) {
      showVariantGenerationSuccessAlert(info);
    } else {
      const id = info.assetCoordinates.id.toString();
      sessionStorage.setItem(AI_SUCCESS_SESSION_KEY, id);
      redirectToPage(getAssetEditorUrl(info.assetCoordinates));
    }
  }, [requestsInProgress]);
  return useMemo(() => ({
    onManualCreateSuccess,
    onAiCreateSuccess,
    loading
  }), [onManualCreateSuccess, onAiCreateSuccess, loading]);
};
export default useContentEditorPostCreation;